<template>
  <div>
    <!-- 悬浮按钮 -->
    <div v-if="showFloatBtn" class="ai-float-btn" @click="openChat">
      <div class="btn-inner">
        <van-icon name="service-o" size="24" color="#fff" />
      </div>
      <span class="btn-label">AI客服</span>
    </div>

    <!-- 聊天弹窗 -->
    <van-popup
      v-model="showChat"
      position="bottom"
      :style="{ height: '80%' }"
      :close-on-click-overlay="true"
      @close="onClose"
    >
      <div class="chat-panel">
        <!-- 头部 -->
        <div class="chat-header">
          <div class="header-left">
            <div class="avatar">AI</div>
            <div class="header-info">
              <div class="title">智能客服</div>
              <div class="status"><i class="dot"></i>在线 · 随时为您解答</div>
            </div>
          </div>
          <div class="header-right">
            <van-icon name="replay" size="18" color="#666" @click="resetChat" title="重新开始" />
            <van-icon name="cross" size="20" color="#666" @click="showChat = false" />
          </div>
        </div>

        <!-- 消息列表 -->
        <div ref="msgList" class="msg-list">
          <div v-for="(msg, index) in messages" :key="index" :class="['msg-row', msg.role]">
            <div v-if="msg.role === 'assistant'" class="msg-avatar ai-avatar">AI</div>
            <div :class="['msg-bubble', msg.role]">
              <div v-if="msg.role === 'assistant'" class="msg-text md-body" v-html="renderMarkdown(msg.content)"></div>
              <span v-else class="msg-text">{{ msg.content }}</span>
            </div>
            <div v-if="msg.role === 'user'" class="msg-avatar user-avatar">我</div>
          </div>
          <!-- AI正在输入 -->
          <div v-if="loading" class="msg-row assistant">
            <div class="msg-avatar ai-avatar">AI</div>
            <div class="msg-bubble assistant typing">
              <span class="dot-typing"></span>
              <span class="dot-typing"></span>
              <span class="dot-typing"></span>
            </div>
          </div>
        </div>

        <!-- 快捷问题 -->
        <div v-if="messages.length <= 1" class="quick-questions">
          <div
            v-for="q in quickQuestions"
            :key="q"
            class="quick-item"
            @click="quickAsk(q)"
          >{{ q }}</div>
        </div>

        <!-- 输入栏 -->
        <div class="chat-input-bar">
          <input
            v-model="inputText"
            class="chat-input"
            type="text"
            placeholder="请输入您的问题..."
            @keyup.enter="sendMessage"
            :disabled="loading"
          />
          <button class="send-btn" :disabled="loading || !inputText.trim()" @click="sendMessage">
            <van-icon name="send" size="18" color="#fff" />
          </button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { sendAiMessage, getAiHistory, resetAiChat } from '@/api/ai'
import { Toast } from 'vant'

export default {
  name: 'AiChatFloat',
  data() {
    return {
      showChat: false,
      messages: [],
      inputText: '',
      loading: false,
      quickQuestions: [
        '怎么查我的订单？',
        '什么时候发货？',
        '怎么退换货？',
        '有什么推荐的商品？'
      ]
    }
  },
  computed: {
    isLogin() {
      const userInfo = this.$store.state.userInfo
      return !!(userInfo && userInfo.id)
    },
    showFloatBtn() {
      // 登录后显示，不在直播页/聊天页显示
      return this.isLogin &&
        this.$route.path !== '/live' &&
        this.$route.path !== '/chat'
    }
  },
  methods: {
    async openChat() {
      if (!this.isLogin) {
        this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } })
        return
      }
      this.showChat = true
      if (this.messages.length === 0) {
        await this.loadHistory()
        // 如果没有历史记录，显示欢迎语
        if (this.messages.length === 0) {
          this.messages.push({
            role: 'assistant',
            content: '您好！我是AI智能客服小助手，您可以问我商品、订单、物流、售后等问题~'
          })
        }
        this.$nextTick(() => this.scrollToBottom())
      }
    },

    async loadHistory() {
      try {
        const res = await getAiHistory(1, 20)
        if (res.data && res.data.length > 0) {
          // 倒序返回的，翻过来
          this.messages = res.data.reverse().map(m => ({
            role: m.role,
            content: m.content
          }))
        }
      } catch (e) {
        // 历史加载失败不影响使用
      }
    },

    async sendMessage() {
      const text = this.inputText.trim()
      if (!text || this.loading) return

      // 添加用户消息
      this.messages.push({ role: 'user', content: text })
      this.inputText = ''
      this.loading = true
      this.scrollToBottom()

      try {
        const res = await sendAiMessage(text)
        if (res.data && res.data.reply) {
          this.messages.push({ role: 'assistant', content: res.data.reply })
          // 如果建议转人工，提示一下
          if (res.data.transferToHuman) {
            setTimeout(() => {
              Toast('正在为您转接人工客服...')
            }, 1500)
          }
        }
      } catch (e) {
        this.messages.push({
          role: 'assistant',
          content: '抱歉，网络开小差了，请稍后再试~'
        })
      } finally {
        this.loading = false
        this.scrollToBottom()
      }
    },

    quickAsk(question) {
      this.inputText = question
      this.sendMessage()
    },

    async resetChat() {
      try {
        await resetAiChat()
        this.messages = [{
          role: 'assistant',
          content: '已开始新对话，请问有什么可以帮您？'
        }]
        this.scrollToBottom()
      } catch (e) {}
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.msgList
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    onClose() {
      this.inputText = ''
    },

    // Markdown渲染（表格、粗体、代码、换行）
    renderMarkdown(text) {
      if (!text) return ''
      // 先转义HTML
      let escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      // 处理Markdown表格
      let lines = escaped.split('\n')
      let result = []
      let i = 0
      while (i < lines.length) {
        let line = lines[i]
        if (line.includes('|') && i + 1 < lines.length && /^[\s|:\-]+$/.test(lines[i + 1])) {
          let headers = line.split('|').filter(c => c.trim()).map(c => c.trim())
          i += 2
          let rows = []
          while (i < lines.length && lines[i].includes('|')) {
            rows.push(lines[i].split('|').filter(c => c.trim()).map(c => c.trim()))
            i++
          }
          let table = '<table style="border-collapse:collapse;width:100%;margin:6px 0;font-size:12px;">'
          table += '<tr>' + headers.map(h => `<th style="border:1px solid #ddd;padding:4px 8px;background:#f8f8f8;">${h}</th>`).join('') + '</tr>'
          rows.forEach(row => {
            table += '<tr>' + row.map(c => `<td style="border:1px solid #ddd;padding:4px 8px;">${c}</td>`).join('') + '</tr>'
          })
          table += '</table>'
          result.push(table)
        } else {
          result.push(line)
          i++
        }
      }
      let html = result.join('\n')

      // **粗体**
      html = html.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
      // `代码`
      html = html.replace(/`(.+?)`/g, '<code style="background:#f4f4f4;padding:1px 4px;border-radius:3px;font-size:12px;">$1</code>')
      // 换行（表格HTML标签后面不额外加br）
      html = html.replace(/\n(?!<)/g, '<br>')
      return html
    }
  }
}
</script>

<style scoped>
.ai-float-btn {
  position: fixed;
  right: 16px;
  bottom: 150px;
  z-index: 998;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}
.btn-inner {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transition: transform 0.2s;
}
.btn-inner:active {
  transform: scale(0.92);
}
.btn-label {
  font-size: 10px;
  color: #667eea;
  margin-top: 2px;
  background: #fff;
  padding: 1px 6px;
  border-radius: 8px;
}

/* 聊天面板 */
.chat-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f5f5;
}
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #eee;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.header-info .title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}
.header-info .status {
  font-size: 11px;
  color: #07c160;
  margin-top: 2px;
}
.header-right {
  display: flex;
  gap: 16px;
  align-items: center;
}
.dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #07c160;
  margin-right: 4px;
}

/* 消息列表 */
.msg-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px 12px;
}
.msg-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 14px;
}
.msg-row.user {
  justify-content: flex-end;
}
.msg-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
  flex-shrink: 0;
}
.ai-avatar {
  background: linear-gradient(135deg, #667eea, #764ba2);
  margin-right: 8px;
}
.user-avatar {
  background: #07c160;
  margin-left: 8px;
}
.msg-bubble {
  max-width: 75%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}
.msg-bubble.assistant {
  background: #fff;
  color: #333;
  border-top-left-radius: 2px;
}
.msg-bubble.user {
  background: #07c160;
  color: #fff;
  border-top-right-radius: 2px;
}
.msg-text {
  white-space: pre-wrap;
}
.md-body {
  line-height: 1.6;
}
.md-body b {
  font-weight: 600;
}
.md-body a {
  color: #667eea;
}

/* 正在输入动画 */
.typing {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 14px 16px;
}
.dot-typing {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #bbb;
  animation: typingBlink 1.4s infinite;
}
.dot-typing:nth-child(2) { animation-delay: 0.2s; }
.dot-typing:nth-child(3) { animation-delay: 0.4s; }
@keyframes typingBlink {
  0%, 80%, 100% { opacity: 0.3; }
  40% { opacity: 1; }
}

/* 快捷问题 */
.quick-questions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-top: 1px solid #eee;
}
.quick-item {
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #667eea;
  border-radius: 16px;
  font-size: 12px;
  color: #667eea;
  cursor: pointer;
}
.quick-item:active {
  background: #667eea;
  color: #fff;
}

/* 输入栏 */
.chat-input-bar {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #fff;
  border-top: 1px solid #eee;
  gap: 8px;
}
.chat-input {
  flex: 1;
  height: 38px;
  border: 1px solid #e5e5e5;
  border-radius: 19px;
  padding: 0 14px;
  font-size: 14px;
  outline: none;
  background: #f7f7f7;
}
.chat-input:focus {
  border-color: #667eea;
  background: #fff;
}
.send-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}
.send-btn:disabled {
  opacity: 0.5;
}
</style>
